export const getBooleanClauseTokens = (value: string | undefined): string[] => {
  if (!value) return [];

  if (!value.includes('(')) return [value];

  const tokens: string[] = [];
  let current = '';
  let depth = 0;

  const pushCurrent = () => {
    const token = current.trim();
    if (token) tokens.push(token);
    current = '';
  };

  for (let index = 0; index < value.length; index += 1) {
    const char = value[index];

    if (char === '(') {
      if (depth > 0) current += char;
      depth += 1;
      continue;
    }

    if (char === ')') {
      depth -= 1;
      if (depth === 0) {
        pushCurrent();
      } else if (depth > 0) {
        current += char;
      }
      continue;
    }

    if (depth === 0) {
      const operator = value.slice(index).match(/^\s+(?:AND|OR)\s+/);
      if (operator) {
        pushCurrent();
        index += operator[0].length - 1;
        continue;
      }
    }

    current += char;
  }

  pushCurrent();
  return tokens;
};

export interface IBooleanClauseSplit {
  /** The operands, in order; one more than `operators`. */
  parts: string[];
  operators: ('AND' | 'OR')[];
}

const isEscapedAt = (value: string, index: number): boolean => {
  let backslashes = 0;
  for (let i = index - 1; i >= 0 && value[i] === '\\'; i -= 1) backslashes += 1;
  return backslashes % 2 === 1;
};

/**
 * The operands and operators at the top level of a clause. Parentheses, quotes and backslash escapes
 * are respected, so `(A OR B) AND "x AND y"` splits once, at its AND.
 */
export const splitTopLevelClause = (value: string): IBooleanClauseSplit => {
  const parts: string[] = [];
  const operators: ('AND' | 'OR')[] = [];
  let current = '';
  let depth = 0;
  let inQuote = false;

  for (let index = 0; index < value.length; index += 1) {
    const char = value[index];
    if (char === '"' && !isEscapedAt(value, index)) inQuote = !inQuote;
    if (!inQuote && !isEscapedAt(value, index)) {
      if (char === '(') depth += 1;
      else if (char === ')') depth -= 1;
      else if (depth === 0) {
        const operator = value.slice(index).match(/^\s+(AND|OR)\s+/);
        if (operator) {
          parts.push(current.trim());
          operators.push(operator[1] as 'AND' | 'OR');
          current = '';
          index += operator[0].length - 1;
          continue;
        }
      }
    }
    current += char;
  }
  parts.push(current.trim());
  return { parts, operators };
};

/** `(…)` or `!(…)` whose parentheses enclose the whole string: the inside and whether it is negated. */
const unwrapGroup = (value: string): { inner: string; negated: boolean } | null => {
  const negated = value.startsWith('!(');
  if (!negated && !value.startsWith('(')) return null;
  const open = negated ? 1 : 0;
  let depth = 0;
  let inQuote = false;
  for (let index = open; index < value.length; index += 1) {
    const char = value[index];
    if (char === '"' && !isEscapedAt(value, index)) inQuote = !inQuote;
    if (inQuote || isEscapedAt(value, index)) continue;
    if (char === '(') depth += 1;
    else if (char === ')') {
      depth -= 1;
      if (depth === 0) {
        if (index !== value.length - 1) return null;
        return { inner: value.slice(open + 1, index), negated };
      }
    }
  }
  return null;
};

/**
 * A clause rendered leaf by leaf, keeping its structure: nested groups keep their parentheses when they hold
 * an operator, a lone parenthesised leaf loses them, and `!(…)` reads as NOT. The boolean input writes
 * `((id) AND field:value) AND ((id) OR …)`, which a one-level split shows as raw ids in brackets.
 */
export const renderBooleanClause = (
  value: string,
  renderLeaf: (leaf: string) => string,
  renderOperator: (operator: 'AND' | 'OR') => string
): string => {
  const trimmed = value.trim();
  const group = unwrapGroup(trimmed);
  if (group) {
    const inner = renderBooleanClause(group.inner, renderLeaf, renderOperator);
    const compound = splitTopLevelClause(group.inner.trim()).parts.length > 1;
    if (group.negated) return compound ? `NOT (${inner})` : `NOT ${inner}`;
    return compound ? `(${inner})` : inner;
  }
  const { parts, operators } = splitTopLevelClause(trimmed);
  if (parts.length === 1) return renderLeaf(trimmed);
  return parts.reduce(
    (out, part, index) => (index === 0 ? '' : `${out}${renderOperator(operators[index - 1])}`) + renderBooleanClause(part, renderLeaf, renderOperator),
    ''
  );
};

/** Every leaf term of a clause, in reading order, with group parentheses and group negation removed. */
export const collectBooleanLeaves = (value: string): string[] => {
  const leaves: string[] = [];
  renderBooleanClause(
    value,
    (leaf) => {
      leaves.push(leaf);
      return leaf;
    },
    () => ''
  );
  return leaves;
};
